import api from '../components/api';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import PrintBulletinHeader from '../components/bulletin/PrintBulletinHeader';
import PrintStudentInfo from '../components/bulletin/PrintStudentInfo';
import PrintGradesTable from '../components/bulletin/PrintGradesTable';
import PrintClassStatistics from '../components/bulletin/PrintClassStatistics';
import PrintDecisionConseil from '../components/bulletin/PrintDecisionConseil';
import PrintQRCode from '../components/bulletin/PrintQRCode';

// Styles CSS communs pour l'impression
const getPrintStyles = () => `
  @page { 
    size: A4; 
    margin: 8mm; 
    padding: 0;
  }
  * { 
    margin: 0; 
    padding: 0; 
    box-sizing: border-box; 
  }
  body { 
    font-family: Arial, sans-serif; 
    font-size: 9pt; 
    line-height: 1.1; 
    color: #000; 
    background: white;
    position: relative;
  }
  table { 
    border-collapse: collapse; 
    width: 100%; 
    font-size: 7pt;
  }
  th, td { 
    border: 1px solid #000; 
    padding: 2px 3px; 
    vertical-align: top; 
    font-size: 7pt;
  }
  th { 
    background-color: #f0f0f0; 
    font-weight: bold; 
    font-size: 7pt;
  }
  .no-border { border: none; }
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .bold { font-weight: bold; }
  .hidden { display: none !important; }
  .print\\:block { display: block !important; }
  .print\\:hidden { display: none !important; }
  
  .bulletin { 
    margin-bottom: 0;
    padding: 0;
    position: relative;
    min-height: 297mm;
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  
  /* QR Code avec positionnement fixe pour l'impression globale */
  div[style*="position: fixed"] {
    position: fixed !important;
    bottom: 10px !important;
    right: 10px !important;
    width: 60px !important;
    height: 60px !important;
    z-index: 1000 !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  /* Suppression des pages blanches */
  .bulletin + .bulletin {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
  
  /* Forcer la continuité entre les bulletins */
  .bulletin:not(:last-child) {
    page-break-after: avoid !important;
    break-after: avoid !important;
    margin-bottom: 0 !important;
  }
  
  .header-section { margin-bottom: 8px; }
  .student-info { margin-bottom: 6px; }
  .grades-table { margin-bottom: 6px; }
  .statistics-section { margin-bottom: 6px; }
  .footer-section { margin-top: 6px; }
  
  @media print {
    .hidden { display: none !important; }
    .print\\:block { display: block !important; }
    .print\\:hidden { display: none !important; }
    
    .bulletin {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
    
    /* Suppression des pages blanches */
    .bulletin + .bulletin {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    
    /* Forcer la continuité entre les bulletins */
    .bulletin:not(:last-child) {
      page-break-after: avoid !important;
      break-after: avoid !important;
      margin-bottom: 0 !important;
    }
    
    /* QR Code avec positionnement fixe pour l'impression globale */
    div[style*="position: fixed"] {
      position: fixed !important;
      bottom: 10px !important;
      right: 10px !important;
      width: 60px !important;
      height: 60px !important;
      z-index: 1000 !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  }
`;

// Générer un bulletin complet en utilisant les composants React
const generateFullBulletin = (fullBulletin, etabData, tableauHonneurData, index, total) => {
  // Créer les composants React exactement comme dans l'impression individuelle
  const bulletinElement = React.createElement('div', {
    className: 'bulletin',
    style: { 
      position: 'relative', 
      minHeight: '297mm',
      padding: 0, 
      margin: 0
    }
  }, [
    // En-tête
    React.createElement(PrintBulletinHeader, {
      key: 'header',
      etablissement: etabData,
      anneeAcademique: fullBulletin?.annee_academique,
      semestre: fullBulletin?.semestre
    }),
    
    // Informations étudiant
    React.createElement(PrintStudentInfo, {
      key: 'student',
      etudiant: fullBulletin?.etudiant,
      classe: fullBulletin?.classe,
      bulletin: fullBulletin
    }),
    
    // Tableau des notes
    React.createElement(PrintGradesTable, {
      key: 'grades',
      details: fullBulletin?.details,
      moyenneSemestre: fullBulletin?.moyenne_semestre
    }),
    
    // Statistiques et Décision côte à côte
    React.createElement('div', {
      key: 'stats-decision',
      style: { 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '8px',
        alignItems: 'flex-start'
      }
    }, [
      React.createElement('div', {
        key: 'stats',
        style: { flex: '1', minWidth: '0' }
      }, React.createElement(PrintClassStatistics, {
        moyenneSemestre: fullBulletin?.moyenne_semestre
      })),
      
      React.createElement('div', {
        key: 'separator',
        style: { 
          width: '1px', 
          backgroundColor: '#000', 
          height: '100%',
          margin: '0 4px'
        }
      }),
      
      React.createElement('div', {
        key: 'decision',
        style: { flex: '1', minWidth: '0' }
      }, React.createElement(PrintDecisionConseil, {
        tableauHonneur: tableauHonneurData
      }))
    ]),
    
    // QR Code avec positionnement fixe
    React.createElement(PrintQRCode, {
      key: 'qr',
      bulletin: fullBulletin,
      etablissement: etabData,
      isGlobalPrint: true
    })
  ]);

  // Convertir en HTML
  return ReactDOMServer.renderToString(bulletinElement);
};

// Service d'impression d'un seul bulletin
export const printSingleBulletin = async (bulletinId) => {
  try {
    const [bulletinResponse, etablissementResponse] = await Promise.all([
      api.get(`/note/bulletins/${bulletinId}/`),
      api.get('etablissement/etablissement-infos/')
    ]);
    
    if (!bulletinResponse.data.success) {
      throw new Error('Erreur lors du chargement du bulletin');
    }
    
    const bulletinData = bulletinResponse.data.data;
    const etabData = etablissementResponse.data;
    
    // Charger le tableau d'honneur
    let tableauHonneurData = null;
    try {
      const tableauResponse = await api.get(`/note/tableau-honneur/${bulletinId}/`);
      if (tableauResponse.data.success && tableauResponse.data.data) {
        tableauHonneurData = tableauResponse.data.data;
      }
    } catch (error) {
      console.error('Erreur lors du chargement du tableau d\'honneur:', error);
    }
    
    return {
      success: true,
      bulletin: bulletinData,
      etablissement: etabData,
      tableauHonneur: tableauHonneurData
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Service d'impression de tous les bulletins
export const printAllBulletins = async (bulletins, classe) => {
  try {
    // Récupérer les informations de l'établissement
    const etabResponse = await api.get('/etablissement/etablissement-infos/');
    const etabData = etabResponse.data;

    // Créer une fenêtre d'impression
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Impression de tous les bulletins - ${classe?.libelle || 'Classe'}</title>
        <style>${getPrintStyles()}</style>
      </head>
      <body>
    `);

    // Traiter chaque bulletin
    for (let i = 0; i < bulletins.length; i++) {
      const bulletin = bulletins[i];
      
      try {
        const response = await api.get(`/note/bulletins/${bulletin.id}/`);
        
        if (!response.data.success) {
          throw new Error('Erreur lors du chargement du bulletin');
        }
        
        const fullBulletin = response.data.data;
        
        // Récupérer le tableau d'honneur
        let tableauHonneurData = null;
        try {
          const thResponse = await api.get(`/note/tableau-honneur/${bulletin.id}/`);
          if (thResponse.data.success && thResponse.data.data) {
            tableauHonneurData = thResponse.data.data;
          }
        } catch (error) {
          console.log('Tableau d\'honneur non trouvé pour bulletin', bulletin.id);
        }

        // Générer le bulletin complet avec les composants React
        const bulletinHTML = generateFullBulletin(fullBulletin, etabData, tableauHonneurData, i, bulletins.length);
        printWindow.document.write(bulletinHTML);
        
      } catch (error) {
        console.error(`Erreur lors de la récupération du bulletin ${bulletin.id}:`, error);
        
        // En cas d'erreur, créer un bulletin d'erreur avec la même structure
        const errorBulletin = {
          etudiant: { nom: bulletin.etudiant?.nom || 'l\'étudiant' },
          classe: { libelle: 'Classe inconnue' },
          annee_academique: { libelle: 'N/A' },
          semestre: { libelle: 'N/A' },
          date_generation: new Date(),
          generer_par: { nom: 'Système' },
          moyenne_semestre: { effectif_classe: 'N/A' },
          details: []
        };
        
        const errorHTML = generateFullBulletin(errorBulletin, etabData, null, i, bulletins.length);
        printWindow.document.write(errorHTML);
      }
    }

    printWindow.document.write(`
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Attendre que le contenu soit chargé puis imprimer
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
    
  } catch (error) {
    throw new Error(`Erreur lors de l'impression globale: ${error.message}`);
  }
};
